import { RouterProvider as TanstackRouterProvider } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { electronApis } from '@/features/electron-api';
import { meetingApis } from '@/features/meetings/meetings.query';
import { tabStore, type TabRoute } from '@/features/tabs/tabs.store';
import { useAnalytics } from '@/hooks/useAnalytics';
import { setHashInElectron } from '@/hooks/useHashRoute';
import { router } from '@/router';
import { settingsStore, TYPE_SCALE_CLASSES } from '@/stores/settings.store';
import { trackEvent } from '@/utils/analytics';
import { AppCapability, isCapable } from '@/utils/capabilities';
import { setRouterInstance } from '@/utils/navigation-service';

// Initialize the navigation service with the router instance
// This breaks the cyclic dependency between auth.ts and router.ts
setRouterInstance(router);

export const RouterProvider = observer(function RouterProvider() {
  useAnalytics();

  useCheckPermissions();
  useNavigationListener();
  useCloseModalsOnNavigate();

  const scale = settingsStore.typeScale;
  const scaleClass = settingsStore.typeScaleClass;

  useEffect(() => {
    document.documentElement.style.setProperty('font-size', scale);
    document.body.classList.remove(...TYPE_SCALE_CLASSES);
    document.body.classList.add(scaleClass);
  }, [scale, scaleClass]);

  return <TanstackRouterProvider router={router} />;
});

/** Closes every modal when the route's path changes, until the returned function runs */
export const closeModalsOnNavigate = (
  routerToWatch: Pick<typeof router, 'subscribe'>
) =>
  routerToWatch.subscribe('onBeforeNavigate', ({ pathChanged }) => {
    if (pathChanged) ModalsManager.closeAll();
  });

function useCloseModalsOnNavigate() {
  useEffect(() => closeModalsOnNavigate(router), []);

  useEffect(() => {
    let confirming = false;
    return router.history.block({
      // defaults to true, which would prompt natively on every reload
      enableBeforeUnload: false,
      blockerFn: async ({ currentLocation, nextLocation }) => {
        if (currentLocation.pathname === nextLocation.pathname) return false;
        const guard = ModalsManager.canCloseAll();
        if (guard.allowed) return false;
        if (confirming) return true;
        confirming = true;
        try {
          const discarded = await ModalsManager.showResolvable(ConfirmDialog, {
            title: guard.reason,
            description: 'Leaving this page closes it and loses your edits.',
            confirmText: 'Discard',
            cancelText: 'Cancel',
          });
          return !discarded;
        } finally {
          confirming = false;
        }
      },
    });
  }, []);
}

function useCheckPermissions() {
  useEffect(() => {
    if (!isCapable(AppCapability.RecordMeeting)) return;

    const check = async () => {
      try {
        await meetingApis.getMacPermissions();
      } catch (_error) {
        // do nothing
      }
    };

    const timeout = setTimeout(check, 2000);
    return () => clearTimeout(timeout);
  }, []);
}

function useNavigationListener() {
  useEffect(() => {
    if (!isCapable(AppCapability.MainWindowExclusive)) return;

    return electronApis.window.mainWindowListenForNavigation(
      async ({ route, params, search, hash, newTab, notificationType }) => {
        if (!isCapable(AppCapability.MainWindowExclusive)) return;
        // Only a click on a push notification carries a type
        if (notificationType) {
          trackEvent('PUSH_NOTIFICATION_CLICKED', {
            notification_type: notificationType,
            route,
          });
        }
        if (newTab && route && tabStore.showTabBar) {
          tabStore.createTab({
            path: route,
            search: search as TabRoute['search'],
          });
        }

        await router.navigate({
          to: route,
          params,
          search,
        });

        if (hash) {
          setHashInElectron(hash);
        }
      }
    );
  }, []);
}
